import { Sparkles, RefreshCw, Loader2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const scoreColor = (score: number) => {
  if (score >= 80) return "text-emerald-400";
  if (score >= 60) return "text-primary";
  if (score >= 40) return "text-amber-400";
  return "text-rose-400";
};

export const scoreLabel = (score: number) => {
  if (score >= 80) return "Strong";
  if (score >= 60) return "Promising";
  if (score >= 40) return "Developing";
  return "Early stage";
};

interface Props {
  score: number | null;
  summary: string | null;
  isLoading: boolean;
  isRescoring: boolean;
  onRescore: () => void;
  lastUpdated?: string | null;
}

const ProjectScoreCard = ({ score, summary, isLoading, isRescoring, onRescore, lastUpdated }: Props) => {
  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Grant Readiness</h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRescore}
          disabled={isLoading || isRescoring}
          className="gap-2"
        >
          {isRescoring ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {score === null ? "Run score" : "Re-grade"}
        </Button>
      </div>

      {isLoading || score === null ? (
        <div className="flex items-center gap-3 text-muted-foreground">
          {isRescoring || isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
          <span className="text-sm">
            {isRescoring ? "Scoring your project…" : "No score yet. Click Run score to generate one."}
          </span>
        </div>
      ) : (
        <div className="flex items-end gap-4 mb-3">
          <div className={`font-display text-6xl font-bold ${scoreColor(score)}`}>{score}</div>
          <div className="pb-2">
            <div className="text-xs text-muted-foreground">/ 100</div>
            <Badge variant="outline" className="mt-1 text-xs">{scoreLabel(score)}</Badge>
          </div>
        </div>
      )}

      {summary && (
        <p className="text-sm text-muted-foreground leading-relaxed">{summary}</p>
      )}
      {lastUpdated && score !== null && (
        <p className="text-xs text-muted-foreground mt-3">Last scored {new Date(lastUpdated).toLocaleString()}</p>
      )}
    </div>
  );
};

export default ProjectScoreCard;